import { DataCollector } from "src/core/contract/DataCollector";
import { ConfigItem } from "src/core/model/TrackerConfig";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";

/**
 * Base collector with lazy evaluation and variable mapping.
 * Mirrors Android BaseDataCollector.
 *
 * Collectors only evaluate a variable if it is mapped in the backend config.
 */
export abstract class BaseDataCollector implements DataCollector {
  abstract collect(): Map<TrackerVariableType, unknown>;

  /**
   * Only call the supplier if the variable is actually mapped in at least one config.
   * This is the "lazy evaluation" pattern from Android.
   */
  protected collectIfNeeded(
    data: Map<TrackerVariableType, unknown>,
    variableType: TrackerVariableType,
    supplier: () => unknown,
    configs?: ConfigItem[],
  ): void {
    if (configs && !BaseDataCollector.isVariableMapped(variableType, configs)) {
      return;
    }
    try {
      data.set(variableType, supplier());
    } catch {
      // Per-variable error isolation — never crash
    }
  }

  /**
   * Maps collected TrackerVariableType → backend field names using config variableMappings.
   */
  static mapVariablesToBackend(
    config: ConfigItem,
    variables: Map<TrackerVariableType, unknown>,
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    const mappings = config.event.variableMappings;

    for (const [backendField, variableName] of Object.entries(mappings)) {
      const variableType = variableName as TrackerVariableType;
      const value = variables.get(variableType);

      if (variableType === TrackerVariableType.CUSTOM_TRIGGER_ARGS) {
        // Extract specific arg by backendField (mirrors Android customArgs.get(variableName))
        if (value && typeof value === "object") {
          result[backendField] = (value as Record<string, unknown>)[backendField] ?? null;
        } else {
          result[backendField] = null;
        }
      } else {
        result[backendField] = value ?? null;
      }
    }

    return result;
  }

  /**
   * Check if a variable type is mapped in any config's variableMappings.
   */
  private static isVariableMapped(variableType: TrackerVariableType, configs: ConfigItem[]): boolean {
    return configs.some((config) => {
      const mappings = config.event.variableMappings;
      return Object.values(mappings).includes(variableType);
    });
  }
}
